import {
  createCalendarDate,
  getRentSortDirection,
  isActiveRentSort,
  isAfter,
  RentCellStatus,
  SortDirection,
  startOfCalendarMonth,
  toCalendarDate,
  type RentSort,
  type RentSortField,
} from '@rentguru/commons-utils';
import clamp from 'lodash/clamp';
import isNil from 'lodash/isNil';

import {
  type RentAriaSort,
  type RentCellChange,
  type RentDensity,
  type RentMatrixCell,
  type RentMatrixLayout,
  type RentMatrixMonth,
  RentMatrixVariant,
} from './rentTypes';

interface RentMatrixVariantMetrics {
  cellVerticalGap: number;
  footerHeight: number;
  headerHeight: number;
  maxColumnWidth: Record<RentDensity, number>;
  maxNameWidth: Record<RentDensity, number>;
  minColumnWidth: Record<RentDensity, number>;
  minNameWidth: Record<RentDensity, number>;
  rowHeight: Record<RentDensity, number>;
  totalWidth: Record<RentDensity, number>;
}

const UNBOUNDED_WIDTH = Number.POSITIVE_INFINITY;
const FIT_NAME_WIDTH = 152;
const NO_TOTAL_COLUMN = 0;
const NO_FOOTER = 0;

const VARIANT_METRICS: Record<RentMatrixVariant, RentMatrixVariantMetrics> = {
  [RentMatrixVariant.FIT]: {
    cellVerticalGap: 6,
    footerHeight: NO_FOOTER,
    headerHeight: 30,
    maxColumnWidth: { comfortable: UNBOUNDED_WIDTH, compact: UNBOUNDED_WIDTH },
    maxNameWidth: { comfortable: FIT_NAME_WIDTH, compact: FIT_NAME_WIDTH },
    minColumnWidth: { comfortable: 72, compact: 18 },
    minNameWidth: { comfortable: FIT_NAME_WIDTH, compact: FIT_NAME_WIDTH },
    rowHeight: { comfortable: 42, compact: 30 },
    totalWidth: { comfortable: NO_TOTAL_COLUMN, compact: NO_TOTAL_COLUMN },
  },
  [RentMatrixVariant.SCROLL]: {
    cellVerticalGap: 8,
    footerHeight: 34,
    headerHeight: 40,
    maxColumnWidth: { comfortable: 168, compact: 88 },
    maxNameWidth: { comfortable: 220, compact: 178 },
    minColumnWidth: { comfortable: 78, compact: 44 },
    minNameWidth: { comfortable: 180, compact: 178 },
    rowHeight: { comfortable: 46, compact: 36 },
    totalWidth: { comfortable: 108, compact: 76 },
  },
};

const HEADER_ROW_COUNT = 1;
const FOOTER_ROW_COUNT = 1;
const NAME_COLUMN_COUNT = 1;
const TOTAL_COLUMN_COUNT = 1;
const MIN_MONTH_SLOTS = 1;
const COMFORTABLE_FIT_MARGIN_IN_PIXELS = 24;
const CHANGE_EPSILON = 0.005;
const FIRST_DAY_OF_MONTH = 1;
const EVEN_ROW_BACKGROUND_CLASS_NAME = 'bg-surface-2';
const ODD_ROW_BACKGROUND_CLASS_NAME = 'bg-card';
const ARIA_SORT_BY_DIRECTION: Record<SortDirection, RentAriaSort> = {
  [SortDirection.ASC]: 'ascending',
  [SortDirection.DESC]: 'descending',
};

const getMinimumWidth = (variant: RentMatrixVariant, density: RentDensity, monthCount: number): number => {
  const metrics = VARIANT_METRICS[variant];
  return metrics.minNameWidth[density] + monthCount * metrics.minColumnWidth[density] + metrics.totalWidth[density];
};

const getMatrixLayout = (
  variant: RentMatrixVariant,
  density: RentDensity,
  viewportWidth: number,
  monthCount: number
): RentMatrixLayout => {
  const metrics = VARIANT_METRICS[variant];
  const totalWidth = metrics.totalWidth[density];
  const monthSlots = Math.max(MIN_MONTH_SLOTS, monthCount);
  const nameWidth = clamp(
    viewportWidth - totalWidth - monthSlots * metrics.minColumnWidth[density],
    metrics.minNameWidth[density],
    metrics.maxNameWidth[density]
  );
  const columnWidth = clamp(
    Math.floor((viewportWidth - nameWidth - totalWidth) / monthSlots),
    metrics.minColumnWidth[density],
    metrics.maxColumnWidth[density]
  );
  const rowHeight = metrics.rowHeight[density];
  return {
    cellHeight: rowHeight - metrics.cellVerticalGap,
    columnWidth,
    contentWidth: Math.max(viewportWidth, nameWidth + monthCount * columnWidth + totalWidth),
    footerHeight: metrics.footerHeight,
    headerHeight: metrics.headerHeight,
    nameWidth,
    rowHeight,
    showsFooter: metrics.footerHeight > NO_FOOTER,
    showsRowTotal: totalWidth > NO_TOTAL_COLUMN,
    totalWidth,
  };
};

const getRowCapacity = (layout: RentMatrixLayout, viewportHeight: number): number =>
  Math.max(0, Math.floor((viewportHeight - layout.headerHeight - layout.footerHeight) / layout.rowHeight));

const fitsComfortableGrid = (
  variant: RentMatrixVariant,
  fitsNow: boolean,
  viewportWidth: number,
  monthCount: number
): boolean => {
  const minimumWidth = getMinimumWidth(variant, 'comfortable', monthCount);
  return fitsNow ? viewportWidth >= minimumWidth : viewportWidth >= minimumWidth + COMFORTABLE_FIT_MARGIN_IN_PIXELS;
};

const resolveRentDensity = (requestedDensity: RentDensity | undefined, fitsComfortable: boolean): RentDensity =>
  requestedDensity ?? (fitsComfortable ? 'comfortable' : 'compact');

const getAriaSort = (sort: RentSort, field: RentSortField, month?: number): RentAriaSort =>
  isActiveRentSort(sort, field, month) ? ARIA_SORT_BY_DIRECTION[getRentSortDirection(sort)] : 'none';

const getRowBackgroundClassName = (index: number): string =>
  index % 2 === 0 ? EVEN_ROW_BACKGROUND_CLASS_NAME : ODD_ROW_BACKGROUND_CLASS_NAME;

const getAriaRowCount = (rowCount: number, showsFooter: boolean): number =>
  rowCount + HEADER_ROW_COUNT + (showsFooter ? FOOTER_ROW_COUNT : 0);

const getBodyAriaRowIndex = (index: number): number => index + HEADER_ROW_COUNT + 1;

const getColumnCount = ({ showsRowTotal }: RentMatrixLayout, monthCount: number): number =>
  NAME_COLUMN_COUNT + monthCount + (showsRowTotal ? TOTAL_COLUMN_COUNT : 0);

const getRentCellChange = (previous: RentMatrixCell, next: RentMatrixCell): RentCellChange | null => {
  if (next.paid - previous.paid > CHANGE_EPSILON) {
    return 'up';
  }
  const turnedLate = next.status === RentCellStatus.LATE && previous.status !== RentCellStatus.LATE;
  if (previous.paid - next.paid > CHANGE_EPSILON || turnedLate) {
    return 'down';
  }
  if (next.status !== previous.status || Math.abs(next.due - previous.due) > CHANGE_EPSILON) {
    return 'status';
  }
  return null;
};

const isCoveredByLaterCall = (
  { coveredDueDate, status }: Pick<RentMatrixCell, 'coveredDueDate' | 'status'>,
  { index, year }: Pick<RentMatrixMonth, 'index' | 'year'>
): boolean =>
  status === RentCellStatus.COVERED &&
  !isNil(coveredDueDate) &&
  isAfter(startOfCalendarMonth(toCalendarDate(coveredDueDate)), createCalendarDate(year, index, FIRST_DAY_OF_MONTH));

export {
  fitsComfortableGrid,
  getAriaRowCount,
  getAriaSort,
  getBodyAriaRowIndex,
  getColumnCount,
  getMatrixLayout,
  getRentCellChange,
  getRowBackgroundClassName,
  getRowCapacity,
  isCoveredByLaterCall,
  resolveRentDensity,
};
